import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import yaml from 'js-yaml';
import Footer from './Footer';
import YamlEditor from './YamlEditor';
import { formatEmojiText } from '../utils/helpers';

function ResultsEditor() {
  const { id } = useParams();
  const [results, setResults] = useState({ results: [] });
  const [yamlText, setYamlText] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [plan, setPlan] = useState(null);

  const loadResults = useCallback(async () => {
    try {
      const response = await axios.get(`/api/plan/${id}`);
      setPlan(response.data.plan);
      setResults(response.data.results || { results: [] });
      
      // Convert results to YAML
      const yamlString = yaml.dump(response.data.results || { results: [] }, { indent: 2 });
      setYamlText(yamlString);
      
      setLoading(false);
    } catch (error) {
      setMessage('Failed to load results');
      setMessageType('error');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const saveResults = async () => {
    try {
      // Parse YAML to validate it
      const parsedResults = yaml.load(yamlText);
      
      // Save the results
      await axios.post(`/api/plan/${id}/save-results`, parsedResults);
      
      setMessage('Results saved successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      if (error.name === 'YAMLException') {
        setMessage('Invalid YAML format: ' + error.message);
      } else {
        setMessage('Failed to save results');
      }
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const resetToOriginal = () => {
    const yamlString = yaml.dump(results, { indent: 2 });
    setYamlText(yamlString);
    setMessage('Reset to original results');
    setMessageType('info');
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>{formatEmojiText('📝', 'Edit Results YAML')}</h1>
        <p>Edit the raw YAML data for test results</p>
        {plan && <p><strong>Plan:</strong> {plan.title}</p>}
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <Link to="/" className="btn btn-secondary" style={{ marginRight: '1rem' }}>
          ← Back to Home
        </Link>
        <Link to={`/dashboard/${id}`} className="btn btn-primary" style={{ marginRight: '1rem' }}>
          📊 Back to Dashboard
        </Link>
        <Link to={`/plan/${id}`} className="btn btn-secondary">
          🧪 Run Tests
        </Link>
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Results YAML Editor</h2>
          <div>
            <button 
              className="btn btn-secondary" 
              onClick={resetToOriginal}
              style={{ marginRight: '1rem' }}
            >
              🔄 Reset
            </button>
            <button className="btn btn-primary" onClick={saveResults}>
              💾 Save Results
            </button>
          </div>
        </div>
        
        <div className="form-group">
          <label>Results YAML</label>
          <YamlEditor
            value={yamlText}
            onChange={setYamlText}
            placeholder="Results YAML will appear here..."
            height="600px"
            onFormat={(formatted) => {
              setYamlText(formatted);
              setMessage('YAML formatted successfully!');
              setMessageType('success');
              setTimeout(() => setMessage(''), 3000);
            }}
            onValidate={(result) => {
              if (result.valid) {
                setMessage('Results YAML is valid and ready to save!');
                setMessageType('success');
              } else {
                setMessage(`YAML validation failed: ${result.error}`);
                setMessageType('error');
              }
              setTimeout(() => setMessage(''), 3000);
            }}
          />
        </div>
        
        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem' }}>
          <p><strong>⚠️ Warning:</strong> Editing results directly can affect dashboard analytics and test history.</p>
          <p><strong>💡 Tip:</strong> You can add, remove, or modify test sessions, step results, and user data.</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ResultsEditor; 